import AppLayout from "@/components/AppLayout";
import ClinicMemberSoloRouteGuard from "@/components/ClinicMemberSoloRouteGuard";
import ClinicRoleRouteGuard from "@/components/ClinicRoleRouteGuard";
import ParentFamiliaLinkedGuard from "@/components/ParentFamiliaLinkedGuard";
import PendingPaymentGuard from "@/components/PendingPaymentGuard";
import SecretaryModuleRouteGuard from "@/components/SecretaryModuleRouteGuard";
import SecretaryRouteGuard from "@/components/SecretaryRouteGuard";
import TeamChatRouteGuard from "@/components/TeamChatRouteGuard";
import TherapistFinanceRouteGuard from "@/components/TherapistFinanceRouteGuard";
import AppRootRedirect from "@/app/AppRootRedirect";
import CadastroLegacyRedirect from "@/app/CadastroLegacyRedirect";
import MarketingLegalRedirect from "@/app/MarketingLegalRedirect";
import { AccessLevel, type RouteConfig } from "@/app/routes/types/routeAccess";
import { Roles } from "@/domains/shared/models/roles";
import { ClinicRoles } from "@/domains/multi/clinicRoles";
import { AuthLayout, LoginLayout } from "@/domains/shared/layouts";
import NotFound from "@/domains/shared/pages/NotFound/NotFound";
import { lazy } from "react";
import { Navigate } from "react-router";

const Login = lazy(() => import("@/domains/shared/pages/Login"));
const Register = lazy(() => import("@/domains/shared/pages/Register"));
const RegisterParent = lazy(() => import("@/domains/shared/pages/RegisterParent"));
const RegisterSecretary = lazy(() => import("@/domains/shared/pages/RegisterSecretary"));
const RegisterClinic = lazy(() => import("@/domains/shared/pages/RegisterClinic"));
const RegisterClinicSecretary = lazy(() => import("@/domains/shared/pages/RegisterClinicSecretary"));
const RegisterClinicInvite = lazy(() => import("@/domains/shared/pages/RegisterClinicInvite"));
const ForgotPassword = lazy(() => import("@/domains/shared/pages/ForgotPassword"));
const VerifyEmail = lazy(() => import("@/domains/shared/pages/VerifyEmail"));
const TrialReactivation = lazy(() => import("@/domains/shared/pages/TrialReactivation"));
const TrialReactivationPaywall = lazy(() => import("@/domains/shared/pages/TrialReactivationPaywall"));

const ParentDashboard = lazy(() => import("@/domains/patient/pages/Dashboard"));
const ParentAgenda = lazy(() => import("@/domains/patient/pages/Agenda"));
const ParentEvolution = lazy(() => import("@/domains/patient/pages/Evolution"));
const ParentGoals = lazy(() => import("@/domains/patient/pages/Goals"));
const ParentOrientations = lazy(() => import("@/domains/patient/pages/Orientations"));
const ParentPhotosAndVideos = lazy(() => import("@/domains/patient/pages/PhotosAndVideos"));
const ParentMeetingMinutes = lazy(() => import("@/domains/patient/pages/MeetingMinutesPage"));
const ParentObservations = lazy(() => import("@/domains/patient/pages/Observations"));
const ParentRequests = lazy(() => import("@/domains/patient/pages/Requests"));
const ParentChat = lazy(() => import("@/domains/patient/pages/Chat"));
const ParentProfile = lazy(() => import("@/domains/patient/pages/Profile"));
const ParentFamily = lazy(() => import("@/domains/patient/pages/Family"));
const ParentSettings = lazy(() => import("@/domains/patient/pages/ParentSettings"));
const ParentFinance = lazy(() => import("@/domains/patient/pages/Finance"));

const TherapistDashboard = lazy(() => import("@/domains/therapist/Dashboard"));
const TherapistAgenda = lazy(() => import("@/domains/therapist/Agenda"));
const TherapistPatients = lazy(() => import("@/domains/therapist/Patients"));
const TherapistPatientDetail = lazy(() => import("@/domains/therapist/PatientDetail"));
const TherapistRequests = lazy(() => import("@/domains/therapist/Requests"));
const TherapistChat = lazy(() => import("@/domains/therapist/Chat"));
const TherapistTeamChat = lazy(() => import("@/domains/therapist/TeamChat"));
const TherapistTeamChatScheduleSettings = lazy(() => import("@/domains/therapist/TeamChatScheduleSettings"));
const TherapistProfile = lazy(() => import("@/domains/therapist/Profile"));
const TherapistSettings = lazy(() => import("@/domains/therapist/Settings"));
const TherapistBillingSettings = lazy(() => import("@/domains/therapist/TherapistBillingSettings"));
const TherapistPrivacy = lazy(() => import("@/domains/therapist/TherapistPrivacy"));
const PatientFinanceDashboard = lazy(() => import("@/domains/therapist/PatientFinanceDashboard"));
const PatientFinanceEntries = lazy(() => import("@/domains/therapist/PatientFinanceEntries"));
const TherapistMarketplace = lazy(() => import("@/domains/therapist/TherapistMarketplace"));
const AdministrativeTeamPage = lazy(() => import("@/domains/therapist/AdministrativeTeamPage"));

const AdminTherapists = lazy(() => import("@/domains/administration/pages/AdminTherapists/AdminTherapists"));
const AdminParents = lazy(() => import("@/domains/administration/pages/AdminParents/AdminParents"));
const AdminPlans = lazy(() => import("@/domains/administration/pages/AdminPlans/AdminPlans"));
const AdminEmailTemplates = lazy(() => import("@/domains/administration/pages/AdminEmailTemplates/AdminEmailTemplates"));
const AdminEmailInbox = lazy(() => import("@/domains/administration/pages/AdminEmailInbox"));
const AdminAiReportPrompts = lazy(() => import("@/domains/administration/pages/AdminAiReportPrompts/AdminAiReportPrompts"));
const AdminMarketplaceModules = lazy(
    () => import("@/domains/administration/pages/AdminMarketplaceModules/AdminMarketplaceModules"),
);
const AdminMigrationRequests = lazy(() => import("@/domains/administration/pages/AdminMigrationRequests"));
const AdminSettings = lazy(() => import("@/domains/administration/pages/AdminSettings/AdminSettings"));

const SecretaryHome = lazy(() => import("@/domains/secretary/pages/SecretaryHome"));
const SecretaryProfile = lazy(() => import("@/domains/secretary/pages/SecretaryProfile"));
const SecretaryChatSettings = lazy(() => import("@/domains/secretary/pages/SecretaryChatSettings"));
const SecretaryTeamChat = lazy(() => import("@/domains/secretary/pages/SecretaryTeamChat"));
const SecretaryTeamChatSettings = lazy(
    () => import("@/domains/secretary/pages/SecretaryTeamChat/SecretaryTeamChatSettings"),
);

const MigrateToClinicPage = lazy(() => import("@/domains/multi/pages/MigrateToClinicPage"));
const ClinicTeamPage = lazy(() => import("@/domains/multi/pages/ClinicTeamPage"));
const ClinicBillingPage = lazy(() => import("@/domains/multi/pages/ClinicBillingPage"));
const ClinicMarketplacePage = lazy(() => import("@/domains/multi/pages/ClinicMarketplacePage"));
const ClinicSettingsPage = lazy(() => import("@/domains/multi/pages/ClinicSettingsPage"));
const ClinicDataPage = lazy(() => import("@/domains/multi/pages/ClinicDataPage"));
const ClinicUnitsPage = lazy(() => import("@/domains/multi/pages/ClinicUnitsPage"));

const therapistAuth: RouteConfig["access"] = [
    { level: AccessLevel.AUTHENTICATED, roles: [Roles.Therapist] },
];

export const APP_ROUTES: RouteConfig[] = [
    { path: "/", element: <AppRootRedirect /> },
    { path: "/termos", element: <MarketingLegalRedirect path="/termos" /> },
    { path: "/privacidade", element: <MarketingLegalRedirect path="/privacidade" /> },

    {
        element: <AuthLayout />,
        children: [
            {
                element: <LoginLayout />,
                children: [
                    { path: "/login", element: <Login /> },
                    { path: "/esqueci-senha", element: <ForgotPassword /> },
                ],
            },
            {
                path: "/reativar-conta",
                children: [
                    { index: true, element: <TrialReactivation /> },
                    { path: "plano", element: <TrialReactivationPaywall /> },
                ],
            },
            {
                path: "/cadastro",
                children: [
                    { index: true, element: <Register /> },
                    { path: "responsavel", element: <RegisterParent /> },
                    { path: "administrativo", element: <RegisterSecretary /> },
                    { path: "clinica", element: <Navigate to="/cadastro/multi" replace /> },
                    {
                        path: "clinica/secretaria",
                        element: <CadastroLegacyRedirect to="/cadastro/multi/secretaria" />,
                    },
                    {
                        path: "clinica/convite",
                        element: <CadastroLegacyRedirect to="/cadastro/multi/convite" />,
                    },
                    { path: "multi", element: <RegisterClinic /> },
                    { path: "multi/secretaria", element: <RegisterClinicSecretary /> },
                    { path: "multi/convite", element: <RegisterClinicInvite /> },
                ],
            },
        ],
    },

    {
        access: [
            {
                level: AccessLevel.AUTHENTICATED,
                roles: [Roles.Parent, Roles.Therapist, Roles.Admin, Roles.Secretary],
            },
        ],
        children: [{ path: "/verificar-email", element: <VerifyEmail /> }],
    },

    {
        access: [{ level: AccessLevel.AUTHENTICATED, roles: [Roles.Parent] }],
        children: [
            {
                element: <ParentFamiliaLinkedGuard />,
                children: [
                    {
                        access: [{ level: AccessLevel.EMAIL_VERIFIED }],
                        children: [
                            {
                                path: "/familia",
                                element: <AppLayout />,
                                children: [
                                    { index: true, element: <ParentDashboard /> },
                                    { path: "agenda", element: <ParentAgenda /> },
                                    { path: "pessoas", element: <ParentFamily /> },
                                    { path: "evolucao", element: <ParentEvolution /> },
                                    { path: "objetivos", element: <ParentGoals /> },
                                    { path: "orientacoes", element: <ParentOrientations /> },
                                    { path: "arquivos", element: <ParentPhotosAndVideos /> },
                                    { path: "observacoes", element: <ParentObservations /> },
                                    { path: "atas-de-reuniao", element: <ParentMeetingMinutes /> },
                                    { path: "solicitacoes", element: <ParentRequests /> },
                                    { path: "chat", element: <ParentChat /> },
                                    { path: "perfil", element: <ParentProfile /> },
                                    { path: "configuracoes", element: <ParentSettings /> },
                                    { path: "financeiro", element: <ParentFinance /> },
                                    { path: "*", element: <NotFound /> },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },

    {
        access: [{ level: AccessLevel.AUTHENTICATED, roles: [Roles.Admin] }],
        children: [
            {
                path: "/admin",
                element: <AppLayout />,
                children: [
                    { index: true, element: <Navigate to="terapeutas" replace /> },
                    { path: "terapeutas", element: <AdminTherapists /> },
                    { path: "responsaveis", element: <AdminParents /> },
                    { path: "planos", element: <AdminPlans /> },
                    { path: "emails", element: <AdminEmailTemplates /> },
                    { path: "email-inbox", element: <AdminEmailInbox /> },
                    { path: "relatorios-ia", element: <AdminAiReportPrompts /> },
                    { path: "marketplace", element: <AdminMarketplaceModules /> },
                    { path: "migracoes", element: <AdminMigrationRequests /> },
                    { path: "configuracoes", element: <AdminSettings /> },
                    { path: "*", element: <NotFound /> },
                ],
            },
        ],
    },

    {
        access: [
            ...therapistAuth!,
            { level: AccessLevel.BILLING_ACTIVE },
            { level: AccessLevel.EMAIL_VERIFIED },
        ],
        children: [
            {
                element: <PendingPaymentGuard />,
                children: [
                    {
                        path: "/profissional",
                        element: <AppLayout />,
                        access: [{ level: AccessLevel.COUNCIL_VERIFIED }],
                        children: [
                            { index: true, element: <TherapistDashboard /> },
                            { path: "agenda", element: <TherapistAgenda /> },
                            {
                                element: <TherapistFinanceRouteGuard />,
                                children: [
                                    { path: "financeiro", element: <PatientFinanceDashboard /> },
                                    {
                                        path: "financeiro/pacientes/:patientId",
                                        element: <PatientFinanceEntries />,
                                    },
                                ],
                            },
                            {
                                element: <SecretaryModuleRouteGuard />,
                                children: [
                                    { path: "administrativo", element: <AdministrativeTeamPage /> },
                                    {
                                        path: "configuracoes/chat-equipe",
                                        element: <TherapistTeamChatScheduleSettings />,
                                    },
                                ],
                            },
                            {
                                element: <TeamChatRouteGuard />,
                                children: [{ path: "chat-equipe", element: <TherapistTeamChat /> }],
                            },
                            {
                                path: "pacientes",
                                children: [
                                    { index: true, element: <TherapistPatients /> },
                                    { path: ":id", element: <TherapistPatientDetail /> },
                                ],
                            },
                            { path: "objetivos", element: <Navigate to="pacientes" replace /> },
                            { path: "solicitacoes", element: <TherapistRequests /> },
                            { path: "chat", element: <TherapistChat /> },
                            {
                                path: "configuracoes/cobranca",
                                element: <TherapistBillingSettings />,
                            },
                            { path: "configuracoes/:tab", element: <TherapistSettings /> },
                            { path: "configuracoes", element: <TherapistSettings /> },
                            { path: "privacidade", element: <TherapistPrivacy /> },
                            {
                                element: <ClinicMemberSoloRouteGuard />,
                                children: [
                                    { path: "migrar-para-multi", element: <MigrateToClinicPage /> },
                                    {
                                        path: "migrar-para-clinica",
                                        element: <Navigate to="/profissional/migrar-para-multi" replace />,
                                    },
                                    { path: "marketplace", element: <TherapistMarketplace /> },
                                ],
                            },
                            // Profile must stay reachable without council number
                            {
                                path: "perfil",
                                element: <TherapistProfile />,
                                access: therapistAuth,
                            },
                        ],
                    },
                ],
            },
        ],
    },

    {
        access: [...therapistAuth!, { level: AccessLevel.BILLING_ACTIVE }],
        children: [
            {
                element: <ClinicRoleRouteGuard roles={[ClinicRoles.Manager]} />,
                children: [
                    {
                        path: "/clinica",
                        element: <AppLayout />,
                        children: [
                            { index: true, element: <ClinicDataPage /> },
                            { path: "dados", element: <Navigate to="/clinica" replace /> },
                            { path: "assinatura", element: <ClinicBillingPage /> },
                            { path: "unidades", element: <ClinicUnitsPage /> },
                            { path: "equipe", element: <ClinicTeamPage /> },
                            { path: "marketplace", element: <ClinicMarketplacePage /> },
                            { path: "configuracoes", element: <ClinicSettingsPage /> },
                            {
                                element: <TeamChatRouteGuard />,
                                children: [{ path: "chat-equipe", element: <TherapistTeamChat /> }],
                            },
                        ],
                    },
                    { path: "/multi/equipe", element: <Navigate to="/clinica" replace /> },
                    { path: "/multi/*", element: <Navigate to="/clinica" replace /> },
                    { path: "/multi", element: <Navigate to="/clinica" replace /> },
                ],
            },
        ],
    },

    {
        access: [
            { level: AccessLevel.AUTHENTICATED, roles: [Roles.Secretary] },
            { level: AccessLevel.BILLING_ACTIVE },
            { level: AccessLevel.EMAIL_VERIFIED },
        ],
        children: [
            {
                path: "/administrativo",
                element: <AppLayout />,
                children: [
                    { index: true, element: <SecretaryHome /> },
                    {
                        path: "agenda",
                        element: (
                            <SecretaryRouteGuard permission={["agenda", "view"]}>
                                <TherapistAgenda />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "pacientes",
                        children: [
                            {
                                index: true,
                                element: (
                                    <SecretaryRouteGuard permission={["patientTabs", "generalView", "view"]}>
                                        <TherapistPatients />
                                    </SecretaryRouteGuard>
                                ),
                            },
                            {
                                path: ":id",
                                element: (
                                    <SecretaryRouteGuard permission={["patientTabs", "generalView", "view"]}>
                                        <TherapistPatientDetail />
                                    </SecretaryRouteGuard>
                                ),
                            },
                        ],
                    },
                    {
                        path: "financeiro",
                        element: (
                            <SecretaryRouteGuard requireFinanceMenu>
                                <PatientFinanceDashboard />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "financeiro/pacientes/:patientId",
                        element: (
                            <SecretaryRouteGuard requireFinanceMenu>
                                <PatientFinanceEntries />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "solicitacoes",
                        element: (
                            <SecretaryRouteGuard permission={["requests", "view"]}>
                                <TherapistRequests />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "chat",
                        element: (
                            <SecretaryRouteGuard permission={["chat", "view"]}>
                                <TherapistChat />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "chat-equipe",
                        element: (
                            <SecretaryRouteGuard permission={["teamChat", "view"]}>
                                <SecretaryTeamChat />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "configuracoes/chat",
                        element: (
                            <SecretaryRouteGuard permission={["chatSettings", "view"]}>
                                <SecretaryChatSettings />
                            </SecretaryRouteGuard>
                        ),
                    },
                    {
                        path: "configuracoes/chat-equipe",
                        element: (
                            <SecretaryRouteGuard permission={["chatSettings", "view"]}>
                                <SecretaryTeamChatSettings />
                            </SecretaryRouteGuard>
                        ),
                    },
                    { path: "perfil", element: <SecretaryProfile /> },
                    { path: "*", element: <NotFound /> },
                ],
            },
        ],
    },

    { path: "*", element: <NotFound /> },
];
